import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { loginRequest, isMsalConfigured } from '../config/msalConfig';

const demoAccounts = [
  { role: 'Super Admin', email: 'superadmin@hotel101global.com', password: 'Demo@123', description: 'Full system access across all hotels', color: 'bg-purple-100 text-purple-700' },
  { role: 'Admin', email: 'admin.sg@hotel101global.com', password: 'Demo@123', description: 'Hotel administrator access', color: 'bg-blue-100 text-blue-700' },
  { role: 'Team Lead', email: 'it.lead@hotel101global.com', password: 'Demo@123', description: 'IT team lead access', color: 'bg-green-100 text-green-700' },
  { role: 'Agent', email: 'agent.it@hotel101global.com', password: 'Demo@123', description: 'IT support agent access', color: 'bg-yellow-100 text-yellow-700' },
  { role: 'User', email: 'user.fo@hotel101global.com', password: 'Demo@123', description: 'Front office user access', color: 'bg-gray-100 text-gray-700' },
];

const Login = () => {
  const { login, loginWithSSO, ssoConfig } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  // MSAL hooks - only use if configured
  const msalContext = isMsalConfigured() ? useMsal() : { instance: null, inProgress: InteractionStatus.None };
  const { instance, inProgress } = msalContext;

  const isSsoEnabled = ssoConfig?.microsoft?.enabled && isMsalConfigured();

  const onSubmit = async (data) => {
    setLoading(true);
    await login(data.email, data.password);
    setLoading(false);
  };

  const handleMicrosoftLogin = async () => {
    if (!instance) return;

    setSsoLoading(true);
    try {
      const response = await instance.loginPopup(loginRequest);

      if (response?.idToken) {
        await loginWithSSO(response.idToken);
      }
    } catch (error) {
      if (error.errorCode !== 'user_cancelled') {
        console.error('Microsoft login error:', error);
        toast.error(error.message || 'Microsoft sign-in failed');
      }
    } finally {
      setSsoLoading(false);
    }
  };

  const fillCredentials = (email, password) => {
    setValue('email', email);
    setValue('password', password);
    setShowDemo(false);
  };

  return (
    <div className="relative">
      <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
        Sign in to your account
      </h2>

      {/* Microsoft SSO Button */}
      {isSsoEnabled && (
        <>
          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={ssoLoading || inProgress !== InteractionStatus.None}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {ssoLoading ? (
              <svg className="animate-spin h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
            )}
            <span className="text-gray-700 font-medium">
              {ssoLoading ? 'Signing in...' : 'Sign in with Microsoft'}
            </span>
          </button>

          <p className="mt-2 text-xs text-center text-gray-500">
            For @{ssoConfig.microsoft.allowedDomain} accounts only
          </p>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="label">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className={`input ${errors.password ? 'input-error' : ''}`}
            {...register('password', {
              required: 'Password is required'
            })}
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : null}
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      {/* Demo Credentials Toggle Button */}
      <button
        type="button"
        onClick={() => setShowDemo(!showDemo)}
        className="fixed right-0 top-1/2 -translate-y-1/2 bg-amber-500 hover:bg-amber-600 text-white px-2 py-4 rounded-l-lg shadow-lg transition-all duration-200 z-50 flex items-center gap-1 text-sm font-medium"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        <svg className={`w-4 h-4 transform transition-transform ${showDemo ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Demo Accounts
      </button>

      {/* Slideout Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-40 ${
          showDemo ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-amber-500 text-white p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Demo Accounts</h3>
              <button
                onClick={() => setShowDemo(false)}
                className="p-1 hover:bg-amber-600 rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-amber-100 text-sm mt-1">Click any account to auto-fill</p>
          </div>

          {/* Warning Banner */}
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-xs text-amber-800">
                <strong>For demo purposes only.</strong> These credentials are for testing and demonstration. Do not use in production.
              </p>
            </div>
          </div>

          {/* Account List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {demoAccounts.map((account, index) => (
              <button
                key={index}
                type="button"
                onClick={() => fillCredentials(account.email, account.password)}
                className="w-full text-left p-3 bg-gray-50 hover:bg-primary-50 hover:border-primary-300 border border-gray-200 rounded-lg transition-all duration-150 group"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${account.color}`}>
                    {account.role}
                  </span>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500 mb-1">{account.description}</p>
                <p className="text-xs text-gray-600 font-mono truncate">{account.email}</p>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              All accounts use password: <code className="bg-gray-200 px-1 rounded">Demo@123</code>
            </p>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {showDemo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-30"
          onClick={() => setShowDemo(false)}
        />
      )}
    </div>
  );
};

export default Login;
